import { useEffect, useRef, useState } from 'react';
import { trackRumAction } from '@/shared/observability/rum';
import type {
  CareScoutWidgetCallbacks,
  UseCareScoutWidgetReturn,
  WidgetStatus,
} from '../types/careCosts.types';

const WIDGET_LOAD_TIMEOUT_MS = 10_000;
const WIDGET_URL_FALLBACK = 'https://www.carescout.com/cost-of-care/widget?embed=1&lang=en';
const CARESCOUT_RESIZE_EVENT = 'careScoutCostOfCareWidgetContentHeightUpdate';
const INITIAL_IFRAME_HEIGHT = 1400;

export const useCareScoutWidget = (
  callbacks: CareScoutWidgetCallbacks = {},
): UseCareScoutWidgetReturn => {
  // Derive origin from the same env var the component uses — stays in sync if URL changes
  const widgetUrl = import.meta.env.VITE_CARESCOUT_WIDGET_URL || WIDGET_URL_FALLBACK;
  const carescoutOrigin = new URL(widgetUrl).origin;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(INITIAL_IFRAME_HEIGHT);
  const [widgetStatus, setWidgetStatus] = useState<WidgetStatus>('loading');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountTimestampRef = useRef<number>(Date.now());

  // Hold the latest callbacks in a ref so the effects don't have to re-run on identity changes.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // 10-second timeout: if onLoad has not fired, transition to 'error' (WIDGET-04, D-03, RUM-03)
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setWidgetStatus((prev) => {
        if (prev === 'loading') {
          trackRumAction('care_cost_widget_error', { reason: 'timeout' }); // D-12, RUM-03
          callbacksRef.current.onError?.('timeout');
          return 'error';
        }
        return prev;
      });
    }, WIDGET_LOAD_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // postMessage resize listener (WIDGET-06, WIDGET-07, D-06, D-07)
  // Dep array is [] — setIframeHeight is a stable dispatch from useState; no closure over mutable values.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Origin guard — mandatory security check (D-06); prevents malicious resize from other origins
      if (event.origin !== carescoutOrigin) return;
      // Event type guard — exact CareScout resize event name (T-02-07)
      if (!event.data || event.data.type !== CARESCOUT_RESIZE_EVENT) return;
      // Type guard on height — prevents NaN or string from corrupting iframe height state (T-02-04)
      if (typeof event.data.height !== 'number') return;
      setIframeHeight(event.data.height);
    };

    window.addEventListener('message', handleMessage);
    // Cleanup removes the exact same named reference — prevents listener accumulation (WIDGET-07, D-07, T-02-05)
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const onIframeLoad = () => {
    // Cancel timeout — onLoad fired, no fallback needed (WIDGET-04, D-03)
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Guard: only transition if still loading — ignore late-fire after timeout (WR-01)
    setWidgetStatus((prev) => {
      if (prev !== 'loading') return prev;
      trackRumAction('care_cost_widget_loaded', { // D-11, RUM-02
        loadTimeMs: Date.now() - mountTimestampRef.current,
        widgetUrlDomain: new URL(widgetUrl).hostname, // SC-1: required dimension
      });
      callbacksRef.current.onLoaded?.();
      return 'loaded';
    });
  };

  // Genuine iframe load failure (network/DNS/blocked). Mirrors the timeout path
  // so the fallback shows promptly with reason 'iframe_error' instead of waiting
  // out the 10s timeout.
  const onIframeError = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setWidgetStatus((prev) => {
      if (prev !== 'loading') return prev;
      trackRumAction('care_cost_widget_error', { reason: 'iframe_error' });
      callbacksRef.current.onError?.('iframe_error');
      return 'error';
    });
  };

  return { iframeRef, iframeHeight, widgetStatus, onIframeLoad, onIframeError };
};
